import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ModalComponent from "../../../components/Modal";
import CustomDropdown from "../../../components/CustomDropdown";
import MuiDatePicker from "../../../components/MuiDatePicker";
import TextInput from "../../../components/TextInput";
import TextAreaInput from "../../../components/TextArea";
import {
  requestGetTimeOffByIdAction,
  requestUpdateTimeOffRequestAction,
  resetGetTimeOffByIdState,
  resetUpdateTimeOffRequestState,
  requestCreateTimeOffRequestAction,
  resetCreateTimeOffRequestState,
} from "../../../Redux/TimeOffManagerState/TimeOffManagerActionCreator";

const validationSchema = (isEditMode = false) => Yup.object({
  type: Yup.string().required("Time off type is required"),
  userId: !isEditMode ? Yup.string().required("Employee is required") : Yup.string(),
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date()
    .required("End date is required")
    .min(Yup.ref("startDate"), "End date must be after start date"),
    reason: Yup.string()
    .required("Reason is required")
    .min(10, "Reason must be at least 10 characters")
    .max(500, "Reason cannot exceed 500 characters"),
  submittedDate: Yup.date().required("Submitted date is required"),
});

const TimeOffForm = ({
  show,
  onClose,
  onSubmit,
  timeOff,
  title,
  selectedDate,
  isEditMode = false,
  timeOffId = null,
  usersList = [],
  isViewMode = false,
}) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadedTimeOffData, setLoadedTimeOffData] = useState(null);
  
  const getTimeOffByIdState = useSelector((state) => state.GetTimeOffByIdReducer);
  const updateTimeOffRequestState = useSelector((state) => state.UpdateTimeOffRequestReducer);
  const timeOffTypes = [
    { value: "CASUAL_LEAVE", label: "Casual Leave (CL)" },
    { value: "SICK_LEAVE", label: "Sick Leave (SL)" },
    { value: "PAID_LEAVE", label: "Paid Leave (PL)" },
    { value: "UNPAID_LEAVE", label: "Unpaid Leave (LOP)" },
    { value: "MATERNITY_LEAVE", label: "Maternity Leave" },
    { value: "PUBLIC_HOLIDAY", label: "Public Holiday" },
  ];

  // Use data from API response when in edit or view mode, otherwise use passed timeOff prop
  const timeOffData = (isEditMode || isViewMode) && loadedTimeOffData ? loadedTimeOffData : timeOff;
  
  // Load time off data when in view mode
  useEffect(() => {
    if (isViewMode && timeOffId && show) {
      setLoadedTimeOffData(null); // Reset loaded data
      dispatch(requestGetTimeOffByIdAction(timeOffId));
    }
  }, [isViewMode, timeOffId, show, dispatch]);  

  // Load time off data when in edit mode
  useEffect(() => {
    if (isEditMode && timeOffId && show) {
      setLoadedTimeOffData(null); // Reset loaded data
      dispatch(requestGetTimeOffByIdAction(timeOffId));
    }
  }, [isEditMode, timeOffId, show, dispatch]);

  // Reset loaded data when modal closes
  useEffect(() => {
    if (!show) {
      setLoadedTimeOffData(null);
    }
  }, [show]);

  // Handle get time off by ID response
  useEffect(() => {
    if (getTimeOffByIdState?.response && !getTimeOffByIdState?.loading && !getTimeOffByIdState?.error) {
      setLoadedTimeOffData(getTimeOffByIdState.response);
      dispatch(resetGetTimeOffByIdState());
    }

    if (getTimeOffByIdState?.error && !getTimeOffByIdState?.loading) {
      toast.error("Failed to load time off request details");
      dispatch(resetGetTimeOffByIdState());
    }
  }, [getTimeOffByIdState, dispatch]);

  // Handle update response
  useEffect(() => {
    if (updateTimeOffRequestState?.response && !updateTimeOffRequestState?.loading && !updateTimeOffRequestState?.error) {
      setIsSubmitting(false);
      onClose();
    }

    if (updateTimeOffRequestState?.error && !updateTimeOffRequestState?.loading) {
      setIsSubmitting(false);
    }

    
  }, [updateTimeOffRequestState, dispatch, onClose]);

  const handleSubmit = (values, { resetForm }) => {
    const timeOffData = {
      startDate: moment(values.startDate).format("MM-DD-YYYY"),
      endDate: moment(values.endDate).format("MM-DD-YYYY"),
      type: values.type,
      reason: values.reason,
      submittedDate: moment(values.submittedDate).format("MM-DD-YYYY"),
    };

    if (isEditMode && timeOffId) {
      // Update existing time off request
      setIsSubmitting(true);
      dispatch(requestUpdateTimeOffRequestAction({ id: timeOffId, payload: timeOffData }));
    } else {
      // Create new time off request
      setIsSubmitting(true);
      // userId needs to be sent in the payload for the URL path
      dispatch(requestCreateTimeOffRequestAction({
        userId: values.userId,
        ...timeOffData
      }));
      setIsSubmitting(false);
      dispatch(resetCreateTimeOffRequestState());
    
      onClose();
      resetForm();
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Show loading state while fetching data in edit or view mode
  if ((isEditMode || isViewMode) && getTimeOffByIdState?.loading) {
    return (
      <ModalComponent show={show} onClose={handleClose} title={title}>
        <div className="flex justify-center items-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading time off request details...</p>
          </div>
        </div>
      </ModalComponent>
    );
  }

  return (
    <ModalComponent show={show} onClose={handleClose} title={title}>
      <Formik
        key={(isEditMode || isViewMode) ? `${isEditMode ? 'edit' : 'view'}-${timeOffId}-${loadedTimeOffData ? 'loaded' : 'loading'}` : 'create'}
        initialValues={(() => {
          const initialValues = {
            type: timeOffData?.type || "",
            userId: timeOffData?.userId || "",
            startDate: timeOffData?.startDate ? moment(timeOffData.startDate) : selectedDate ? moment(selectedDate) : null,
            endDate: timeOffData?.endDate ? moment(timeOffData.endDate) : selectedDate ? moment(selectedDate) : null,
            reason: timeOffData?.reason || "",
            submittedDate: timeOffData?.submittedDate ? moment(timeOffData.submittedDate) : moment(),
          };
          
          return initialValues;
        })()}
        validationSchema={validationSchema(isEditMode)}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => {
          const totalDays = values.startDate && values.endDate 
            ? moment(values.endDate).diff(moment(values.startDate), 'days') + 1
            : 0;

          return (
            <Form className="max-h-[85vh] overflow-y-scroll no-scrollbar p-2 md:p-4">
              <div className="space-y-4">
                {/* Time Off Type and Employee - Only show Employee dropdown when adding new request */}
                {isEditMode || isViewMode ? (
                  /* Full width Type dropdown in edit/view mode */
                  <div className="w-full">
                    <CustomDropdown
                      value={timeOffTypes.find(type => type.value === values.type)}
                      customClasses="w-full text-left"
                      name="type"
                      handleChange={(selectedOption) => {
                        setFieldValue("type", selectedOption?.value || "");
                      }}
                      placeholder="Select Time Off Type"
                      options={timeOffTypes}
                      handleGetOptionValue={(option) => option.value}
                      handleGetOptionName={(option) => option.label}
                      legend="Time Off Type"
                      isDisabled={isViewMode}
                    />
                    <ErrorMessage
                      name="type"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                ) : (
                  /* Grid layout for add mode - Employee and Type side by side */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {usersList && usersList.length > 0 && (
                      <div className="w-full">
                        <CustomDropdown
                          value={usersList.find(user => user.id === values.userId)}
                          customClasses="w-full text-left"
                          name="userId"
                          handleChange={(selectedOption) => {
                            setFieldValue("userId", selectedOption?.id || "");
                          }}
                          placeholder="Select Employee"
                          options={usersList}
                          handleGetOptionValue={(option) => option.id}
                          handleGetOptionName={(option) => `${option.firstName} ${option.lastName}`}
                          legend="Employee"
                        />
                        <ErrorMessage
                          name="userId"
                          component="div"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                    )}
                    <div className="w-full">
                      <CustomDropdown
                        value={timeOffTypes.find(type => type.value === values.type)}
                        customClasses="w-full text-left"
                        name="type"
                        handleChange={(selectedOption) => {
                          setFieldValue("type", selectedOption?.value || "");
                        }}
                        placeholder="Select Time Off Type"
                        options={timeOffTypes}
                        handleGetOptionValue={(option) => option.value}
                        handleGetOptionName={(option) => option.label}
                        legend="Time Off Type"
                      />
                      <ErrorMessage
                        name="type"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>
                )}

                {/* Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="w-full">
                    <MuiDatePicker
                      label="Start Date"
                      value={values.startDate ? moment(values.startDate) : null}
                      onChange={(date) => {
                        if (date) {
                          setFieldValue("startDate", date.format("YYYY-MM-DD"));
                          // If end date is before the new start date, reset it
                          if (values.endDate && moment(date).isAfter(moment(values.endDate))) {
                            setFieldValue("endDate", null);
                          }
                        }
                      }}
                      minDate={null}
                      disabled={isViewMode}
                    />
                    <ErrorMessage
                      name="startDate"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>

                  <div className="w-full">
                    <MuiDatePicker
                      label="End Date"
                      value={values.endDate ? moment(values.endDate) : null}
                      onChange={(date) => {
                        if (date) {
                          setFieldValue("endDate", date.format("YYYY-MM-DD"));
                        }
                      }}
                      minDate={values.startDate ? moment(values.startDate) : moment()}
                      disabled={isViewMode}
                    />
                    <ErrorMessage
                      name="endDate"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>

                {/* Submitted Date */}
                <div className="w-1/2">
                  <MuiDatePicker
                    label="Submitted Date"
                    value={values.submittedDate ? moment(values.submittedDate) : null}
                    onChange={(date) => {
                      if (date) {
                        setFieldValue("submittedDate", date.format("YYYY-MM-DD"));
                      }
                    }}
                    minDate={null}
                    disabled={isViewMode}
                  />
                  <ErrorMessage
                    name="submittedDate"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Total Days Display */}
                {totalDays > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Total Days:</span> {totalDays} day{totalDays > 1 ? 's' : ''}
                    </p>
                  </div>
                )}

                {/* Reason */}
                <div className="w-full">
                  <TextAreaInput
                    value={values.reason}
                    name="reason"
                    placeholder="Enter reason for time off request"
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    classes="w-full"
                    legend="Reason"
                    rows={4}
                    isDisabled={isViewMode}
                  />
                 <div className="flex justify-between items-center mt-1">
                    {errors.reason && touched.reason && (
                      <div className="text-red-500 text-sm">
                        {errors.reason}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 ml-auto">
                      {values.reason?.length || 0}/500 characters
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-4">
                  {!isViewMode && (
                    <>
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting || getTimeOffByIdState?.loading || updateTimeOffRequestState?.loading}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting || updateTimeOffRequestState?.loading
                          ? (isEditMode ? "Updating..." : "Submitting...")
                          : isEditMode
                          ? "Update"
                          : "Submit "}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </ModalComponent>
  );
};

export default TimeOffForm;
